import { TiclickTableProvider } from './context/ticlick-table-context-provider';
import TiclickTableFilterSearchInput from './ticlick-table-filter-search-input';
import TiclickTableHeader from './ticlick-table-header';
import TiclickTableRow from './ticlick-table-row';
import TiclickTableTableActions, {
  type ActionItemProps,
} from './ticlick-table-table-actions';
import { TiclickTablePagination } from './ticlick-table-pagination';
import { Table, TableBody } from '@src/design-system/table';
import { TiclickTableSelectionIndicator } from './ticlick-table-selection-indicator';
import { TiclickTableSkeleton } from './ticlick-table-skeleton';

import { TiclickTableFilterBtn } from './ticlick-table-filter-btn';
import { FilterBtnPopover } from '@shared/features/views/components/filter-btn-popover';
import { SavedViewsControl } from './actions/saved-views-control';

import type { SearchInput } from '../search-input';
import {
  memo,
  useRef,
  useEffect,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from 'react';
import type { TiclickTableContextType } from './context/ticlick-table-context';
import type { Table as TTable } from '@tanstack/react-table';
import { CurrentFilters } from './current-filters';
import { ActiveFiltersBar } from '@shared/features/views/components/active-filters-bar';

import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';

import { TableEmptyState } from './ticlick-table-empty-state';

export type TiclickTableProps<T> = {
  renderFilterBtn?: (comp: () => JSX.Element) => JSX.Element;
  actions?: ActionItemProps<T>[];
  props?: { searchInputProps?: ComponentProps<typeof SearchInput> };
  withFilterBtn?: boolean;
  toolbarExtra?: ReactNode;
} & TiclickTableContextType<T>;

export default function TiclickTable<T>({
  table,
  actions,
  withFilterBtn,
  renderFilterBtn,
  props,
  toolbarExtra,
}: TiclickTableProps<T>) {
  const columns = table?.getAllColumns();

  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  const tableViewEntity = table.options.meta?.identifyingData.viewEntityType;
  const tableQueryData = table.options.meta?.queryData;

  const isPending = Boolean(tableQueryData?.isPending);
  const rows = table.getRowModel().rows;

  if (!columns || columns.length === 0) {
    return <div>Table misconfiguration: No columns provided.</div>;
  }

  return (
    <TiclickTableProvider table={table}>
      <div className='ticlick-table space-y-4'>
        <div className='ticlick-table-tool-tip flex items-center gap-3'>
          <div className='flex min-w-0 flex-1 items-center gap-4 overflow-x-auto'>
            <div className='flex shrink-0 group'>
              <TiclickTableFilterSearchInput
                {...props?.searchInputProps}
                className='group-has-[button]:rounded-r-none group-has-[button]:z-2'
              />

              {withFilterBtn && !renderFilterBtn && (
                <FilterBtnPopover FilterBtn={TiclickTableFilterBtn} />
              )}

              {withFilterBtn &&
                renderFilterBtn &&
                renderFilterBtn(TiclickTableFilterBtn)}
            </div>

            {tableViewEntity && <SavedViewsControl />}

            <TiclickTableSelectionIndicator />

            <CurrentFilters />

            {toolbarExtra}
          </div>

          {actions && (
            <div className='ml-auto shrink-0'>
              <TiclickTableTableActions actions={actions} />
            </div>
          )}
        </div>

        <ActiveFiltersBar />

        <div className='bg-white relative overflow-hidden rounded-md border border-primary-200'>
          <Table
            containerRef={scrollElementRef}
            className='table-fixed w-full border-separate border-spacing-0 [&_th]:border-pale-border [&_th]:border-b'
          >
            <TiclickTableHeader />

            {isPending ? (
              <TiclickTableSkeleton />
            ) : !rows.length ? (
              <TableBody className='h-120'>
                <div className='absolute left-0 right-0 flex items-center justify-center'>
                  <TableEmptyState />
                </div>
              </TableBody>
            ) : table.getState().columnSizingInfo.isResizingColumn ? (
              <MemoizedTableBody
                table={table}
                scrollElementRef={scrollElementRef}
              />
            ) : (
              <TiclickTableBody
                table={table}
                scrollElementRef={scrollElementRef}
              />
            )}
          </Table>
        </div>

        <TiclickTablePagination />
      </div>
    </TiclickTableProvider>
  );
}

const TiclickTableBody = <T,>({
  table,
  scrollElementRef,
}: {
  table: TTable<T>;
  scrollElementRef: RefObject<HTMLDivElement | null>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getAllColumns().length;
  const scrollEl = scrollElementRef.current;

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => 64,
    overscan: 5,
    getItemKey: (index: number) => rows[index]?.id ?? index,
  });

  // Force virtualizer to recalculate after mount when scroll element is ready
  useEffect(() => {
    if (scrollEl && rowVirtualizer) {
      rowVirtualizer.measure();
    }
  }, [scrollEl, rowVirtualizer]);

  const virtualItems = scrollEl ? rowVirtualizer.getVirtualItems() : [];
  const fallbackRows = rows.slice(0, 25);
  const shouldFallback =
    rows.length > 0 && (!scrollEl || virtualItems.length === 0);

  const paddingTop = virtualItems.length > 0 ? virtualItems[0]!.start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() -
        virtualItems[virtualItems.length - 1]!.end
      : 0;

  return (
    <TableBody>
      {shouldFallback ? (
        fallbackRows.map((row) => (
          <TiclickTableRow
            key={row.id}
            row={row}
            rowIndex={row.index}
          />
        ))
      ) : (
        <>
          {!!paddingTop && (
            <tr>
              <td
                colSpan={columnCount}
                style={{ height: paddingTop }}
              />
            </tr>
          )}

          {virtualItems.map((virtualRow: VirtualItem) => {
            const row = rows[virtualRow.index];
            if (!row) return null;
            return (
              <TiclickTableRow
                key={row.id}
                row={row}
                rowIndex={row.index}
                virtualRow={virtualRow}
                rowVirtualizer={rowVirtualizer}
              />
            );
          })}

          {!!paddingBottom && (
            <tr>
              <td
                colSpan={columnCount}
                style={{ height: paddingBottom }}
              />
            </tr>
          )}
        </>
      )}
    </TableBody>
  );
};

const MemoizedTableBody = memo(TiclickTableBody, (prev, next) => {
  return prev.table.options.data === next.table.options.data;
}) as typeof TiclickTableBody;
